import Image from 'app/Components/Shared/Image/image';
import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import classNames from 'classnames';
import 'swiper/swiper.scss';
import HiddenStopAndStartButton from 'app/utilities/HiddenStopAndStartButton';
import {useInView} from 'react-intersection-observer';
import {isMobile} from 'react-device-detect';
import {BackgroundImageProps} from 'app/utilities/ImageProps';
import TagName, {Tags} from 'app/Components/Shared/TagName';

export type HeaderCarouselItem = {
  imageDesktop: BackgroundImageProps;
  imageMobile: BackgroundImageProps;
  videoMobile: string;
  videoDesktop: string;
  text: string;
  subtitle: string;
  highlightText: string;
  buttonLink: string;
  buttonLinkTitle: string;
  titleHeadlineTag: number;
  subtitleHeadlineTag: number;
  blockButton: any;
};

type HeaderCarouselProps = {
  items: HeaderCarouselItem[];
};

const HeaderCarousel = ({items}: HeaderCarouselProps) => {
  const [enter, setEnter] = useState(null);
  const [leave, setLeave] = useState(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [swiperIsStop, setSwiperIsStop] = useState<Boolean>(false);
  const [swiperAutoPlay, setSwiperAutoPlay] = useState('Stop');
  const {ref} = useInView();

  var designModeNumber = -1;
  var designText = 'להצטרפות';

  if (items[0].blockButton != null) {
    designText = (items[0].blockButton[0] as any).text.value;
    designModeNumber = (items[0].blockButton[0] as any).buttonDesignMode.value;
  }

  useEffect(() => {
    if (swiperIsStop) {
      videoRef.current?.pause();
      setSwiperAutoPlay('Start');
    } else {
      videoRef.current?.play();
      setSwiperAutoPlay('Stop');
    }
  }, [swiperIsStop]);

  const videoRefCallback = useCallback((node: HTMLVideoElement) => {
    videoRef.current = node;
    ref(node);
  }, []);

  let evPage = useMemo(() => {
    let segment = window.location.pathname.split('/').filter(Boolean).pop();
    if (segment?.trim().toLowerCase() == 'ev') return true;
    return false;
  }, []);

  useEffect(() => {
    if (items?.length > 0) {
      const total = items.length;
      let pageY = 0;
      let idx = 0;
      let timeout: NodeJS.Timeout = null;

      const activate = (offset: number) => {
        if (offset != 0) setLeave(items[idx] ?? null);
        idx += offset;
        setEnter(items[idx]);
        timeout = setTimeout(() => {
          setLeave(null);
          timeout = null;
        }, 500);
      };

      const deltaYHandler = (deltaY: number, event: Event) => {
        if (timeout != null) {
          event.preventDefault();
        } else if (deltaY > 0 && idx < total - 1) {
          activate(1);
          event.preventDefault();
        } else if (deltaY < 0 && idx > 0 && window.scrollY == 0) {
          activate(-1);
          event.preventDefault();
        }
      };

      const wheelHandler = (event: WheelEvent) => {
        deltaYHandler(event.deltaY, event);
      };

      const touch = {
        startHandler: (event: TouchEvent) => (pageY = event.touches[0].pageY),
        endHandler: () => (pageY = 0),
        moveHandler: (event: TouchEvent) => {
          deltaYHandler(pageY - event.touches[0].pageY, event);
        },
      };

      activate(0);

      document.body.addEventListener('wheel', wheelHandler, {passive: false});
      document.body.addEventListener('touchstart', touch.startHandler, {passive: false});
      document.body.addEventListener('touchend', touch.endHandler, {passive: false});
      document.body.addEventListener('touchmove', touch.moveHandler, {passive: false});

      return () => {
        document.body.removeEventListener('wheel', wheelHandler);
        document.body.removeEventListener('touchstart', touch.startHandler);
        document.body.removeEventListener('touchend', touch.endHandler);
        document.body.removeEventListener('touchmove', touch.moveHandler);
      };
    } else {
      setLeave(null);
      setEnter(null);
    }
  }, [items]);

  return (
    <div className='HeaderCarousel'>
      <HiddenStopAndStartButton className='' onClick={() => setSwiperIsStop((prevState) => !prevState)} swiperAutoPlay={swiperAutoPlay} />
      {items.map((item, i) => (
        <div className={classNames('HeaderCarousel__item', {enter: item === enter, leave: item == leave})} key={i}>
          {(item.videoMobile != null || item.videoDesktop != null) && (
            <video ref={videoRefCallback} className='HeaderCarousel__video' autoPlay loop muted playsInline>
              <source src={isMobile ? item.videoMobile : item.videoDesktop} type='video/mp4' />
            </video>
          )}

          {item.videoDesktop == null && item.videoMobile == null && (
            <div className='HeaderCarousel__image' {...(isMobile ? item.imageMobile : item.imageDesktop)} />
          )}

          <div className='HeaderCarousel__description'>
            <TagName tagName={item.titleHeadlineTag} isTitle className={`HeaderCarousel__title ${evPage ? 'white-color' : ''}`}>
              {item.text}
            </TagName>

            <TagName tagName={item.subtitleHeadlineTag} className={`HeaderCarousel__subtitle ${evPage ? 'white-color' : ''}`}>
              {item.subtitle}
            </TagName>
            {item.buttonLink != null && item.buttonLinkTitle != '' && (
              <div className='HeaderCarousel__button-container'>
                <a className={`HeaderCarousel__button-container__button ${getButtonClassByName(designModeNumber)}`} href={item.buttonLink}>
                  {designText}
                  <Image src={require('./images/icons.svg')} />
                </a>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const getButtonClassByName = (designIndex) => {
  let name = '';
  switch (designIndex) {
    case 0:
      name = 'purple-button';
      break;
    case 1:
      name = 'black-button';
      break;
    case 2:
      name = 'white-button';
      break;
    case -1:
      name = 'purple-button';
      break;
  }

  return name;
};

export default HeaderCarousel;
